import config from 'config';
import { action, computed, observable, reaction } from 'mobx';
import MediahubStore from 'stores/MediahubStore';
// Import Stores
import ViewStore from 'stores/ViewStore';
import verge from 'verge';

// Some defaults
const MAX_ITEMS_PER_ROW = 2;
const ITEM_RATIO        = 0.5625;
const BUFFER            = 2;
const GUTTER            = 20;
const MOBILE_HEIGHT     = 80;
const MOBILE_WIDTH      = config.mobileWidth;
const MIN_ITEM_WIDTH    = (MOBILE_WIDTH + 1) / 2;

/**
 * InfiniteWallStore Class
 */
class InfiniteWallStore {
  rAF;

  @observable scrollY    = 0;
  @observable scrollD    = 0;
  @observable wrapperW   = 0;
  @observable itemsCount = 0;

  constructor() {
    // autorun(() => {
    //   this.updateScroll(ViewStore.parentScrollTop);
    // });

    reaction(
      () => this.shouldLoadNextBatch,
      () => {
        if(this.shouldLoadNextBatch) {
          MediahubStore.loadNextBatch();
        }
      },
    );
  }

  @computed
  get isMobile() {
    return this.wrapperW <= MOBILE_WIDTH;
  }

  @computed
  get itemsPerRow() {
    const itemsPerRow = Math.floor(this.wrapperW / MIN_ITEM_WIDTH);
    return Math.min(itemsPerRow, MAX_ITEMS_PER_ROW);
  }

  @computed
  get rowCount() {
    return Math.ceil(this.itemsCount / this.itemsPerRow) || 0;
  }

  @computed
  get wrapperH() {
    return Math.ceil(this.rowCount * this.itemH);
  }

  @computed
  get itemW() {
    const width = (this.wrapperW / this.itemsPerRow) || 0;
    return _.isEqual(this.itemsPerRow, 1)
      ? width
      : width - GUTTER;
  }

  @computed
  get itemH() {
    return this.isMobile
      ? (ITEM_RATIO * this.itemW) / 2 + GUTTER
      : (ITEM_RATIO * this.itemW) + MOBILE_HEIGHT;
  }

  @computed
  get scrollTop() {
    return Math.max(0, (this.scrollY - this.scrollD)) || 0;
  }

  @computed
  get rowsAbove() {
    return Math.max(0, Math.floor(this.scrollTop / this.itemH)) || 0;
  }

  @computed
  get visibleRows() {
    const winH = ViewStore.parentWinHeight || verge.viewportH();
    return Math.ceil(((this.rowsAbove * this.itemH) + Math.min(winH, this.wrapperH)) / this.itemH) || 0;
  }

  @computed
  get indexStart() {
    return Math.max(0, Math.min((this.itemsCount - 1), ((this.rowsAbove - BUFFER) * this.itemsPerRow))) || 0;
  }

  @computed
  get indexEnd() {
    return Math.min(this.itemsCount, ((this.visibleRows + BUFFER) * this.itemsPerRow)) || this.itemsCount;
  }

  @computed
  get shouldLoadNextBatch() {
    return this.indexEnd >= this.itemsCount;
  }

  @computed
  get indexes() {
    // Computes an array of indexes for layouting purposes
    let indexes = [];
    let i       = this.indexStart;
    for(i; i < this.indexEnd; i++) {
      indexes.push(i);
    }
    return indexes;
  }

  getPosition(index) {
    const gutter = !_.isEqual(this.itemsPerRow, 1)
      ? this.itemsPerRow * GUTTER / (this.itemsPerRow - 1)
      : 0;

    const rowIndex = Math.floor(index / this.itemsPerRow);
    const posX     = ((index - (rowIndex * this.itemsPerRow)) * (this.itemW + gutter));
    const posY     = (rowIndex * this.itemH);

    return { posX, posY };
  }

  @action
  updateItemsCount(itemsCount) {
    this.itemsCount = itemsCount;
  }

  @action
  updateDimensions(elem) {
    const scrollY = verge.scrollY() || ViewStore.parentScrollTop;
    const rect    = verge.rectangle(elem);

    this.wrapperW = rect.width;
    this.scrollD  = verge.scrollY() + rect.top + ViewStore.parentTopOffset;
    this.scrollY  = scrollY;
  }

  @action
  updateScroll() {
    // We don't have to worry about unnecessary updates
    const scrollY = verge.scrollY() || ViewStore.parentScrollTop;
    this.scrollY  = scrollY;

    // Put's scroll updates in requestAnimationFrame
    this.rAF = window.requestAnimationFrame(
      this.updateScroll.bind(this),
    );

    // It returns a function because it's possible then
    // to callcancelAnimationFrame in the wall component
    return function() {
      window.cancelAnimationFrame(this.rAF);
    }.bind(this);
  }
}

const wallStore = new InfiniteWallStore();
export default wallStore;
